import React from 'react';
import { Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { authService } from '../../../services/auth';
import type { OrderProduct } from '../../../types/order';
import type { OrderCostPlanLine, OrderCostPlanLineStock } from '../../../types/orderWarehouseSummary';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatLocalizedPercent } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { getOrderProductDisplayName } from '../orderProductDisplay';
import type { OrderStepContext } from './orderStepContext';
import { HiddenValue, InfoList, LineValuePending, LineValuesFailedNote } from './OrderProcessParts';
import { OrderLineNameWithDimensions, useQuantityFormatter } from './OrderProcessTables';
import { ORDER_MARGIN_STAGE_LABEL_KEYS } from './orderMarginStage';
import { useMarginIncompleteText } from './OrderProcessHeader';
import { getOrderMarginQueueExplanation } from './orderMarginQueue';

/** Grosz precision for comparing a part's amount with the column value. */
const roundGrosz = (value: number) => Math.round(value * 100) / 100;

/**
 * „Skąd koszt” of ONE line, from the WP5 plan (the plan lines of the
 * full-margin readers, otherwise the per-line cost basis of the planned-margin
 * preview): issued WZ, FIFO parts with their PZ numbers, the part priced at the
 * average and why (queue), or an unknown cost. Used by the „Skąd koszt” table
 * and by the margin card of steps 1–3. Nothing is recomputed.
 * `hideBasis`: the margin card already shows the cost basis as a tag in the
 * same cell, so the preview-only reading (no plan) does not repeat it.
 */
export function OrderLineCostSource({ ctx, product, hideBasis = false }: { ctx: OrderStepContext; product: OrderProduct; hideBasis?: boolean }) {
  const { t, i18n } = useTranslation(['orders']);
  const quantity = useQuantityFormatter();
  const money = (value: number | null | undefined) => (value == null ? '—' : formatCurrencyLocalized(value, 'PLN', i18n.language));
  const showCosts = ctx.marginVisibility.showCosts;
  const plan = ctx.warehouse?.finance?.plan;
  const planLine = (id: string) => plan?.lines.find((line) => line.id === id);
  const fifoLine = (id: string) => ctx.fifoLines.find((line) => line.id === id);

  /**
   * „Na stanie 100 m², 110 m² czeka dla wcześniej potwierdzonych…” beside a line priced at the average.
   * `none` adds nothing: the „średni koszt (szacunek)” tag and the „425 szt. po średnim koszcie” line
   * already say the cost is the average, so the note is not repeated on every line.
   */
  const queueNote = (stock: OrderCostPlanLineStock | null | undefined, unit: string | null, average: { price: number | null; unit: string | null } | null) => {
    const explanation = getOrderMarginQueueExplanation(stock);
    if (!explanation || explanation.kind === 'none') return null;
    // The sentence ends with „.” in the copy; drop the abbreviation's own dot
    // („szt.”) so it does not read „szt..”.
    const price = showCosts && average?.price != null
      ? ` ${money(average.price)}${average.unit ? ` / ${formatUnit(average.unit, i18n.language)}` : ''}`.replace(/\.$/, '')
      : '';
    return (
      <span key="queue" className="zk-source__muted" data-testid="zk-margin-queue">
        {t(`orders:process.margin.queue.${explanation.kind}`, {
          onHand: quantity(explanation.onHand, unit),
          held: quantity(explanation.held, unit),
          shortfall: quantity(explanation.shortfall, unit),
          price,
        })}
      </span>
    );
  };

  /**
   * The FIFO part text with its PZ number in a nowrap span (the number must not break mid-token).
   * The number links to the PZ card only for readers of warehouse documents (the PZ route's gate)
   * and only when the part carries the PZ document id; otherwise it stays plain text.
   */
  const fifoPartText = (quantityText: string, pzNumber: string, pzDocumentId: string | null | undefined) => {
    const MARK = '\u2063';
    const canOpenPz = Boolean(pzDocumentId) && authService.canViewWarehouseDocuments();
    const [head, ...rest] = t('orders:process.margin.source.fifo', { quantity: quantityText, pz: MARK }).split(MARK);
    return [
      head,
      ...rest.flatMap((piece, index) => [
        // eslint-disable-next-line react/no-array-index-key
        <span key={`pz-${index}`} className="zk-source__pz">
          {canOpenPz ? <Link to={`/app/pz/${pzDocumentId}`}>{pzNumber}</Link> : pzNumber}
        </span>,
        piece,
      ]),
    ];
  };

  const line: OrderCostPlanLine | undefined = planLine(product.id);
  if (line) {
    // Amounts per part show how the column adds up. A single part hides its
    // amount only when it IS the column value (the column also carries the
    // planned freight); otherwise the amount stays, so no goods cost goes missing.
    const hasAveragePart = line.averageCostPLN != null && line.averageQuantity != null;
    const costPartCount = (line.issuedCostPLN > 0 ? 1 : 0) + line.fifo.length + (hasAveragePart ? 1 : 0);
    const columnCost = ctx.fifoLines.find((item) => item.id === product.id)?.costPLN;
    const showPartAmount = (amount: number | null | undefined) => showCosts && (
      costPartCount > 1 || amount == null || columnCost == null || roundGrosz(amount) !== roundGrosz(columnCost)
    );
    const parts: React.ReactNode[] = [];
    if (line.issuedCostPLN > 0) {
      parts.push(<span key="issued">{t('orders:process.margin.source.issued')}{showPartAmount(line.issuedCostPLN) && <> · {money(line.issuedCostPLN)}</>}</span>);
    }
    line.fifo.forEach((part, index) => parts.push(
      // eslint-disable-next-line react/no-array-index-key
      <span key={`fifo-${index}`}>
        {fifoPartText(quantity(part.quantity, part.unit), part.pzNumber || '—', part.pzDocumentId)}
        {showPartAmount(part.costPLN) && <> · {money(part.costPLN)}</>}
        {part.source !== 'FIFO' && <> <Tag>{t(`orders:warehouseFlow.source.${part.source}`)}</Tag></>}
      </span>,
    ));
    if (hasAveragePart) {
      parts.push(
        <span key="average">
          {t('orders:process.margin.source.average', { quantity: quantity(line.averageQuantity, line.averageUnit) })}
          {showCosts && line.averagePurchasePricePLN != null && (
            <span className="zk-source__muted">{' '}({money(line.averagePurchasePricePLN)}{line.averagePurchasePriceUnit ? ` / ${formatUnit(line.averagePurchasePriceUnit, i18n.language)}` : ''})</span>
          )}
          {showPartAmount(line.averageCostPLN) && <> · {money(line.averageCostPLN)}</>}
        </span>,
      );
      const note = queueNote(line.stock, line.unit, { price: line.averagePurchasePricePLN, unit: line.averagePurchasePriceUnit });
      if (note) parts.push(note);
    }
    if (line.costPending) {
      parts.push(
        <span key="unknown" className="zk-source__unknown">
          {line.pendingQuantity != null
            ? t('orders:process.margin.source.unknown', { quantity: quantity(line.pendingQuantity, line.unit) })
            : t('orders:process.margin.source.unknownNoQuantity')}
          {line.pendingReason && ` — ${t(`orders:warehouseFlow.pendingReason.${line.pendingReason}`)}`}
        </span>,
      );
    }
    return <div className="zk-source">{parts.length ? parts : <span className="zk-source__muted">{t('orders:process.margin.source.nothingOpen')}</span>}</div>;
  }
  const fifo = fifoLine(product.id);
  if (!fifo && ctx.fifoLoading) return <LineValuePending />;
  if (!fifo?.costBasis) return <span className="zk-source__muted">—</span>;
  const note = (fifo.costBasis === 'AVERAGE_ESTIMATE' || fifo.costBasis === 'MIXED')
    ? queueNote(fifo.stock, product.unit ?? null, { price: fifo.averagePurchasePricePLN ?? null, unit: fifo.averagePurchasePriceUnit ?? null })
    : null;
  const reason = fifo.costBasis === 'UNKNOWN' && fifo.pendingReason ? t(`orders:warehouseFlow.pendingReason.${fifo.pendingReason}`) : null;
  if (hideBasis) {
    if (!note && !reason) return null;
    return (
      <div className="zk-source">
        {reason && <span className="zk-source__unknown">{reason}</span>}
        {note}
      </div>
    );
  }
  return (
    <div className="zk-source">
      <span className={fifo.costBasis === 'WAREHOUSE' ? '' : 'zk-source__unknown'}>
        {t(`orders:process.margin.basis.${fifo.costBasis}`)}
        {reason && ` — ${reason}`}
      </span>
      {note}
    </div>
  );
}

/**
 * „Skąd koszt” — the margin of each line explained from the WP5 plan
 * (decision 2026-10-04 pkt 8). Numbers are the backend's: the per-line cost
 * and margin from the FIFO preview of the saved lines, the cost parts from
 * the warehouse summary plan (full-margin readers). Nothing is recomputed.
 *
 * Handlowiec: the cost basis label and the line margin percent, no cost
 * amounts. Operacje: no margin at all.
 */
export default function OrderMarginExplanation({ ctx, mode = 'plan' }: { ctx: OrderStepContext; mode?: 'plan' | 'invoice' }) {
  const { t, i18n } = useTranslation(['orders']);
  const money = (value: number | null | undefined) => (value == null ? '—' : formatCurrencyLocalized(value, 'PLN', i18n.language));
  const percent = (value: number | null | undefined) => (value == null ? '—' : formatLocalizedPercent(value, i18n.language));
  const { order, marginVisibility, margin } = ctx;
  const showCosts = marginVisibility.showCosts;
  const finance = ctx.warehouse?.finance;
  const products = order.productsLoaded ? order.products : [];
  const fifoLine = (id: string) => ctx.fifoLines.find((line) => line.id === id);
  const pending = (id: string) => Boolean(ctx.fifoLoading) && !fifoLine(id);
  const incompleteText = useMarginIncompleteText(margin);

  const footer: Array<[React.ReactNode, React.ReactNode]> = [];
  if (showCosts && finance) {
    footer.push([t('orders:process.margin.transport'), money(finance.transportCostPLN)]);
    footer.push([t('orders:process.margin.other'), money(finance.otherCostPLN)]);
  }
  const totalLabel = t(ORDER_MARGIN_STAGE_LABEL_KEYS[margin.stage]);
  const totalValue = margin.costState === 'UNKNOWN'
    ? t('orders:process.margin.unknown')
    : [
      marginVisibility.showAmount && margin.amount != null ? money(margin.amount) : null,
      margin.percent != null ? percent(margin.percent) : null,
      margin.costState === 'PARTIAL' ? t('orders:process.margin.incomplete') : null,
    ].filter(Boolean).join(' · ') || t('orders:process.margin.unknown');
  footer.push([<strong key="label">{totalLabel}</strong>, <span key="value" data-testid="zk-margin-total">{totalValue}</span>]);

  return (
    <div data-testid={`zk-margin-explanation-${mode}`}>
      {ctx.fifoFailed && <LineValuesFailedNote />}
      <Table<OrderProduct>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={products}
        locale={{ emptyText: t('orders:process.lines.empty') }}
        columns={[
          {
            title: t('orders:process.lines.product'),
            key: 'product',
            render: (_v, product) => (
              <OrderLineNameWithDimensions name={getOrderProductDisplayName(product, product.name)} product={product} testId="zk-margin-line-dimensions" />
            ),
          },
          { title: t('orders:process.margin.sourceColumn'), key: 'source', render: (_v, product) => <OrderLineCostSource ctx={ctx} product={product} /> },
          {
            title: t('orders:process.margin.costColumn'),
            key: 'cost',
            align: 'right',
            render: (_v, product) => (showCosts
              ? (pending(product.id) ? <LineValuePending /> : <span className="zk-num">{money(fifoLine(product.id)?.costPLN)}</span>)
              : <HiddenValue reason="costs" />),
          },
          {
            title: t('orders:process.margin.marginColumn'),
            key: 'margin',
            align: 'right',
            render: (_v, product) => {
              const fifo = fifoLine(product.id);
              if (pending(product.id)) return <LineValuePending />;
              return (
                <span className="zk-num" data-testid="zk-line-margin">
                  {showCosts && fifo?.marginPLN != null && <>{money(fifo.marginPLN)}<br /></>}
                  <span className={showCosts ? 'zk-table-note' : ''}>{percent(fifo?.marginPercent)}</span>
                </span>
              );
            },
          },
        ]}
      />
      <div className="zk-margin-footer">
        <InfoList rows={footer} />
      </div>
      {margin.costIncomplete && <p className="zk-margin-footer__hint" data-testid="zk-margin-incomplete">{incompleteText}</p>}
    </div>
  );
}
